// Shared SPA lifecycle helper (plan A9): run a cleanup once an element leaves
// the DOM. The one place a MutationObserver lives — screens import this instead
// of rolling their own.

/** Run `cb` once `el` is removed from the DOM. If `el` is already detached when
 *  called (e.g. a superseded navigation discarded it), run the cleanup now
 *  rather than re-scheduling forever — otherwise the controller's engine /
 *  document listeners would outlive the tab. */
export function onDisconnect(el: HTMLElement, cb: () => void): void {
  if (!el.isConnected) {
    cb();
    return;
  }
  const obs = new MutationObserver(() => {
    if (!el.isConnected) {
      obs.disconnect();
      cb();
    }
  });
  // Controls can be nested several levels below the screen that is removed.
  obs.observe(document.documentElement, { childList: true, subtree: true });
}

/** Subscribe only after mounting, and release even when an ancestor unmounts. */
export function mountedSubscription(el: HTMLElement, subscribe: () => () => void): void {
  requestAnimationFrame(() => {
    if (el.isConnected) onDisconnect(el, subscribe());
  });
}

export function listenWhileMounted(el: HTMLElement, name: string, listener: () => void): void {
  mountedSubscription(el, () => {
    listener(); // another control may have changed the preference before mount
    window.addEventListener(name, listener);
    return () => window.removeEventListener(name, listener);
  });
}
